import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FaCheck, FaTimes, FaUser, FaBuilding, FaEnvelope, FaPhone, FaClock, FaSearch, FaUserCheck, FaUserTimes } from 'react-icons/fa';
import AdminLayout from '../../components/AdminLayout';

export default function UserApprovals() {
  const { API, user } = useContext(AppContext);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Reject dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    if (user) {
      fetchPendingUsers();
    }
  }, [user]);

  const fetchPendingUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/auth/admin/pending-users?admin_id=${user.id}`);
      setPendingUsers(response.data);
    } catch (error) {
      console.error('Error fetching pending users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId, userName) => {
    if (!window.confirm(`Approuver le compte de ${userName} ?`)) {
      return;
    }

    try {
      await axios.post(`${API}/auth/admin/approve-user/${userId}?admin_id=${user.id}`);
      toast.success(`Compte de ${userName} approuvé avec succès`);
      fetchPendingUsers();
    } catch (error) {
      console.error('Error approving user:', error);
      toast.error('Erreur lors de l\'approbation');
    }
  };

  const openRejectDialog = (userData) => {
    setSelectedUser(userData);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!selectedUser) return;

    try {
      await axios.post(
        `${API}/auth/admin/reject-user/${selectedUser.id}?admin_id=${user.id}&reason=${encodeURIComponent(rejectReason)}`
      );
      toast.success(`Compte de ${selectedUser.name} rejeté`);
      setRejectDialogOpen(false);
      fetchPendingUsers();
    } catch (error) {
      console.error('Error rejecting user:', error);
      toast.error('Erreur lors du rejet');
    }
  };

  // Filter users
  const filteredUsers = pendingUsers.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#0A2540]">Approbation des Comptes</h1>
            <p className="text-gray-500 mt-1">
              {pendingUsers.length} compte(s) en attente d'approbation
            </p>
          </div>
          <Button onClick={fetchPendingUsers} variant="outline">
            Actualiser
          </Button>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative max-w-md">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Rechercher par nom, email ou entreprise..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Pending Users List */}
        {loading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#635BFF] mx-auto"></div>
              <p className="mt-4 text-gray-500">Chargement...</p>
            </CardContent>
          </Card>
        ) : filteredUsers.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FaUserCheck className="text-6xl text-green-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700">Aucun compte en attente</h3>
              <p className="text-gray-500 mt-2">Tous les comptes ont été traités.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredUsers.map(pendingUser => (
              <Card key={pendingUser.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                    {/* User Info */}
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-[#635BFF]/10 rounded-full flex items-center justify-center">
                          <FaUser className="text-[#635BFF] text-xl" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-[#0A2540]">{pendingUser.name}</h3>
                          <Badge className="bg-yellow-100 text-yellow-800">En attente</Badge>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <FaEnvelope className="text-[#00D4FF]" />
                          <span>{pendingUser.email}</span>
                        </div>
                        {pendingUser.phone && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <FaPhone className="text-[#635BFF]" />
                            <span>{pendingUser.phone}</span>
                          </div>
                        )}
                        {pendingUser.company && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <FaBuilding className="text-[#0A2540]" />
                            <span>{pendingUser.company}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-gray-600">
                          <FaClock className="text-gray-400" />
                          <span>Inscrit le {new Date(pendingUser.created_at).toLocaleDateString('fr-FR')}</span>
                        </div>
                      </div>

                      {pendingUser.role && pendingUser.role !== 'user' && (
                        <Badge variant="outline" className="border-[#635BFF] text-[#635BFF]">
                          Rôle demandé: {pendingUser.role}
                        </Badge>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 self-end md:self-center">
                      <Button
                        onClick={() => handleApprove(pendingUser.id, pendingUser.name)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <FaCheck className="mr-2" />
                        Approuver
                      </Button>
                      <Button
                        onClick={() => openRejectDialog(pendingUser)}
                        variant="outline"
                        className="border-red-500 text-red-500 hover:bg-red-50"
                      >
                        <FaTimes className="mr-2" />
                        Rejeter
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <FaUserTimes />
                Rejeter le compte
              </DialogTitle>
            </DialogHeader>
            
            {selectedUser && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-medium">{selectedUser.name}</p>
                  <p className="text-sm text-gray-500">{selectedUser.email}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Motif du rejet (optionnel)
                  </label>
                  <Textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Ex: Informations incomplètes, compte suspect..."
                    rows={3}
                  />
                </div>

                <p className="text-sm text-gray-500">
                  L'utilisateur recevra un email l'informant du rejet.
                </p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleReject} className="bg-red-600 hover:bg-red-700">
                Confirmer le rejet
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
